import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals";
import { s25Resource } from "../../../util/s25.resource";
import { EventMicroService } from "../../../services/event.micro.service";
import { S25Util } from "../../../util/s25-util";
import { ReservationService } from "../../../services/reservation.service";
import { CustomAttributes } from "../../../pojo/CustomAttributes";
import { UserprefService } from "../../../services/userpref.service";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { inject } from "@angular/core";
import { StudentEngagementUtil } from "../student.engagement.util";
import { LoginStore } from "../../../stores/login.store";
import { AttendanceService, attendanceState } from "./attendance.service";
import { Attendance } from "./se.attendance.util";
import { S25WsMicroService } from "../../../services/MicroServices/S25WsMicroService";

type State = { eventId: number | undefined; rsrvId: number | undefined };

export const SeEventCheckinStore = signalStore(
    withState<State>({
        eventId: undefined,
        rsrvId: undefined,
    }),
    withProps((store) => ({
        _loginStore: inject(LoginStore),
        _attendanceService: inject(AttendanceService),
    })),
    withProps((store) => ({
        _eventDetails: s25Resource({
            params: () => store.eventId(),
            loader: async ({ params: eventId, abortSignal }) => {
                const event = await EventMicroService.getEventDetailById([eventId], false, [
                    "attributes",
                    "organizations",
                ]);
                return event?.content?.data?.items?.[0];
            },
        }),
        _reservation: s25Resource({
            params: () => store.rsrvId(),
            loader: async ({ params: rsrvId, abortSignal }) => {
                return await ReservationService.getSingleReservation(rsrvId);
            },
        }),
        _userAttendance: s25Resource({
            if: () => store._loginStore.loggedIn(),
            params: () => ({ eventId: store.eventId(), rsrvId: store.rsrvId() }),
            loader: async ({ params: { eventId, rsrvId }, abortSignal }) => {
                if (!eventId || !rsrvId) return undefined;
                return store._attendanceService.getUserAttendanceForReservation(eventId, rsrvId, { abortSignal });
            },
        }),
        _dateFormat: s25Resource({ loader: () => UserprefService.getS25Dateformat() }),
        _timeFormat: s25Resource({ loader: () => UserprefService.getS25Timeformat() }),
        _dateTimeFormat: s25Resource({ loader: () => UserprefService.getS25DateTimeformat() }),
    })),
    withProps((store) => ({
        _dateFormats: () => {
            if (!store._dateFormat.value() || !store._timeFormat.value() || !store._dateTimeFormat.value())
                return undefined;
            return {
                date: store._dateFormat.value()!,
                time: store._timeFormat.value()!,
                dateTime: store._dateTimeFormat.value()!,
            };
        },
    })),
    withMethods((store) => ({
        _formatDateTime: (date: string | undefined) => {
            const dateTimeFormat = store._dateTimeFormat.value();
            if (!dateTimeFormat || !date) return undefined;
            return S25Datefilter.transform(date, dateTimeFormat);
        },
    })),
    withComputed((store) => ({
        isLoading: () => store._eventDetails.resource.isLoading() || store._reservation.resource.isLoading(),
        hasError: () => !!store._eventDetails.resource.error() || !!store._reservation.resource.error(),
        eventNotFound: () => {
            const cause = <any>store._eventDetails.resource.error()?.cause;
            const err = <S25WsMicroService.Error>cause?.error?.content?.error;
            return err?.reason === "EV_E_NOTFOUND";
        },
        eventName: () => store._reservation.value()?.event_name,
        orgId: () => store._eventDetails.value()?.organizations?.primary,
        eventImageUrl: () =>
            store._eventDetails
                .value()
                ?.attributes?.find((attr) => attr.attributeId === CustomAttributes.system.HotEventImage.id),
        date: () => {
            const start = store._formatDateTime(store._reservation.value()?.event_start_dt);
            const end = store._formatDateTime(store._reservation.value()?.event_end_dt);
            if (!start || !end) return undefined;
            return `${start} - ${end}`;
        },
        occurrence: () => {
            const dateFormats = store._dateFormats();
            const reservation = store._reservation.value();
            if (!dateFormats || !reservation) return undefined;
            const date = StudentEngagementUtil.formatDate(
                reservation.event_start_dt,
                reservation.event_end_dt,
                dateFormats,
            );
            return { reservationId: store.rsrvId(), date };
        },
        locations: () =>
            S25Util.array.forceArray(store._reservation.value()?.space_reservation).map((space) => space.space_name),
        canCheckIn: () => {
            const state = store._userAttendance.value()?.attendeeState;
            return !state || Attendance.State.isAtMost(state, attendanceState.REGISTERED);
        },
        checkedIn: () => {
            const attendance = store._userAttendance.value();
            if (!attendance) return false;
            return Attendance.State.isAtLeast(attendance.attendeeState, attendanceState.CHECKED_IN);
        },
        checkedOut: () => {
            const attendance = store._userAttendance.value();
            return attendance?.attendeeState === attendanceState.CHECKED_OUT;
        },
    })),
    withMethods((store) => ({
        setReservation: (eventId: number, rsrvId: number) => patchState(store, { eventId, rsrvId }),
        checkIn: async () => {
            const eventId = store.eventId();
            const rsrvId = store.rsrvId();
            if (!eventId || !rsrvId) return;
            const [ok, err] = await S25Util.Maybe(
                store._attendanceService.updateAttendance(eventId, "CHECKED_IN", { occurrences: [rsrvId] }),
            );
            if (!!err) {
                S25Util.showError(err);
            } else {
                store._userAttendance.resource.reload();
            }
        },
        checkOut: async () => {
            const eventId = store.eventId();
            const rsrvId = store.rsrvId();
            if (!eventId || !rsrvId) return;
            const [ok, err] = await S25Util.Maybe(
                store._attendanceService.updateAttendance(eventId, "CHECKED_OUT", { occurrences: [rsrvId] }),
            );
            if (!!err) {
                S25Util.showError(err);
            } else {
                store._userAttendance.resource.reload();
            }
        },
    })),
);
